import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useWebStore } from '../../context/WebStoreContext';
import { executeRecaptcha } from '../../utils/recaptcha';

const EcommerceAuthModal = ({
  show,
  onHide,
  initialMode = 'register', // 'register' | 'login'
  onSuccess,
  companyName,
  storeConfig
}) => {
  const { activeCompanyId } = useWebStore();
  const companyIdToUse = storeConfig?.company_id || activeCompanyId || (new URLSearchParams(window.location.search).get('company')) || undefined;
  const authReqOptions = companyIdToUse ? {
    params: { company: companyIdToUse },
    headers: { 'X-Company-ID': String(companyIdToUse) }
  } : {};

  const [activeTab, setActiveTab] = useState(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Formulario de Registro
  const [registerData, setRegisterData] = useState({
    d_razon: '',
    d_whatsapp: '',
    d_email: '',
    d_passnu: '',
    d_domic: '',
    d_local: '',
    c_posta: '',
    d_obser: ''
  });

  // Formulario de Login / Identificación por Email y Contraseña
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const resetAllFormStates = () => {
    setErrorMessage('');
    setLoginEmail('');
    setLoginPassword('');
    setShowRegisterPassword(false);
    setShowLoginPassword(false);
    setRegisterData({
      d_razon: '',
      d_whatsapp: '',
      d_email: '',
      d_passnu: '',
      d_domic: '',
      d_local: '',
      c_posta: '',
      d_obser: ''
    });
  };

  useEffect(() => {
    if (show) {
      setActiveTab(initialMode);
      resetAllFormStates();
    }
  }, [show, initialMode]);

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!registerData.d_razon.trim()) {
      setErrorMessage('Por favor ingresá tu nombre y apellido o razón social.');
      return;
    }
    if (!registerData.d_email.trim()) {
      setErrorMessage('Por favor ingresá tu correo electrónico.');
      return;
    }
    if (!registerData.d_whatsapp.trim()) {
      setErrorMessage('Por favor ingresá tu número de WhatsApp.');
      return;
    }
    if (!registerData.d_passnu.trim()) {
      setErrorMessage('Por favor creá una contraseña para tu cuenta (máx. 15 caracteres).');
      return;
    }
    if (registerData.d_passnu.length < 4) {
      setErrorMessage('La contraseña debe tener al menos 4 caracteres.');
      return;
    }

    setIsLoading(true);
    try {
      const recaptchaToken = await executeRecaptcha('registro_ecommerce');
      const payload = {
        ...registerData,
        recaptcha_token: recaptchaToken
      };
      const res = await axios.post('/api/ecommerce/registro-cliente/', payload, authReqOptions);
      const cliente = res.data.cliente;

      Swal.fire({
        title: '¡Registro Exitoso!',
        text: `¡Bienvenido/a, ${cliente.d_razon}! Ya podés ver todos los precios y armar tu pedido.`,
        icon: 'success',
        confirmButtonColor: '#0d6efd',
        confirmButtonText: 'Ver Catálogo con Precios'
      });

      if (onSuccess) {
        onSuccess(cliente);
      }
      resetAllFormStates();
      onHide();
    } catch (err) {
      console.error('Error en registro:', err);
      const msg = err.response?.data?.error || 'Ocurrió un error al registrar tus datos. Por favor intentá nuevamente.';
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!loginEmail.trim()) {
      setErrorMessage('Por favor ingresá tu correo electrónico registrado.');
      return;
    }
    if (!loginPassword.trim()) {
      setErrorMessage('Por favor ingresá tu contraseña.');
      return;
    }

    setIsLoading(true);
    try {
      const recaptchaToken = await executeRecaptcha('login_ecommerce');
      const res = await axios.post('/api/ecommerce/login-cliente/', {
        email: loginEmail.trim(),
        password: loginPassword.trim(),
        recaptcha_token: recaptchaToken
      }, authReqOptions);
      const cliente = res.data.cliente;

      Swal.fire({
        title: '¡Sesión Iniciada!',
        text: `¡Hola de nuevo, ${cliente.d_razon}!`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });

      if (onSuccess) {
        onSuccess(cliente);
      }
      resetAllFormStates();
      onHide();
    } catch (err) {
      console.error('Error en login:', err);
      const msg = err.response?.data?.error || 'No pudimos verificar tus credenciales. Por favor revisá tu correo y contraseña.';
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalHide = () => {
    resetAllFormStates();
    onHide();
  };

  return (
    <Modal show={show} onHide={handleModalHide} centered size="md" className="ecom-auth-modal">
      <Modal.Header closeButton className="border-0 px-4 pt-4 pb-2">
        <div className="d-flex align-items-center gap-3">
          <div className="bg-primary text-white rounded-3 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '46px', height: '46px' }}>
            <i className="bi bi-shield-lock-fill fs-5"></i>
          </div>
          <div>
            <h5 className="fw-bold mb-0 text-dark">Acceso a Clientes</h5>
            <span className="small text-muted">{companyName || storeConfig?.logoText || storeConfig?.footer?.companyName || 'Catálogo Online'} &bull; Tienda Oficial</span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="px-4 py-3">
        {/* Selector Segmentado de Modo (Pestañas limpias) */}
        <div className="ecom-segmented-control mb-3">
          <button
            type="button"
            className={`ecom-segment-btn ${activeTab === 'register' ? 'active' : ''}`}
            onClick={() => { setActiveTab('register'); setErrorMessage(''); }}
          >
            <i className="bi bi-person-plus-fill"></i>
            <span>Registrarme</span>
          </button>
          <button
            type="button"
            className={`ecom-segment-btn ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => { setActiveTab('login'); setErrorMessage(''); }}
          >
            <i className="bi bi-box-arrow-in-right"></i>
            <span>Ya tengo cuenta</span>
          </button>
        </div>

        {errorMessage && (
          <Alert variant="danger" className="py-2.5 px-3 small d-flex align-items-center gap-2 mb-3 rounded-3 border-danger-subtle">
            <i className="bi bi-exclamation-circle-fill text-danger fs-6 flex-shrink-0"></i>
            <span>{errorMessage}</span>
          </Alert>
        )}

        {/* Pestaña: Registro de Nuevo Cliente */}
        {activeTab === 'register' && (
          <Form onSubmit={handleRegisterSubmit}>
            <p className="small text-muted mb-3.5 lh-sm">
              Completá tus datos para acceder a los precios exclusivos de nuestro catálogo y realizar pedidos por WhatsApp.
            </p>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold text-dark mb-1.5">
                Nombre y Apellido / Razón Social <span className="text-danger">*</span>
              </Form.Label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0 text-muted">
                  <i className="bi bi-person"></i>
                </span>
                <Form.Control
                  type="text"
                  name="d_razon"
                  placeholder="Ej. Juan Pérez / Librería Central"
                  value={registerData.d_razon}
                  onChange={handleRegisterChange}
                  required
                  className="border-start-0"
                />
              </div>
            </Form.Group>

            <div className="row g-3 mb-3">
              <div className="col-12 col-sm-6">
                <Form.Group>
                  <Form.Label className="small fw-semibold text-dark mb-1.5">
                    WhatsApp / Celular <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0 text-success">
                      <i className="bi bi-whatsapp"></i>
                    </span>
                    <Form.Control
                      type="tel"
                      name="d_whatsapp"
                      placeholder="Ej. 11 4455-6677"
                      value={registerData.d_whatsapp}
                      onChange={handleRegisterChange}
                      required
                      className="border-start-0"
                    />
                  </div>
                </Form.Group>
              </div>
              <div className="col-12 col-sm-6">
                <Form.Group>
                  <Form.Label className="small fw-semibold text-dark mb-1.5">
                    Correo Electrónico <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0 text-muted">
                      <i className="bi bi-envelope"></i>
                    </span>
                    <Form.Control
                      type="email"
                      name="d_email"
                      placeholder="ejemplo@correo.com"
                      value={registerData.d_email}
                      onChange={handleRegisterChange}
                      required
                      className="border-start-0"
                    />
                  </div>
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold text-dark mb-1.5">
                Crear Contraseña (máx. 15 caracteres) <span className="text-danger">*</span>
              </Form.Label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0 text-muted">
                  <i className="bi bi-lock-fill"></i>
                </span>
                <Form.Control
                  type={showRegisterPassword ? "text" : "password"}
                  name="d_passnu"
                  maxLength={15}
                  placeholder="Tu contraseña de acceso"
                  value={registerData.d_passnu}
                  onChange={handleRegisterChange}
                  required
                  className="border-start-0 border-end-0"
                />
                <span
                  className="input-group-text bg-white border-start-0 text-muted"
                  style={{ cursor: 'pointer', transition: 'color 0.15s ease' }}
                  onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                  title={showRegisterPassword ? "Ocultar contraseña" : "Ver contraseña"}
                >
                  <i className={`bi ${showRegisterPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                </span>
              </div>
            </Form.Group>

            <div className="row g-3 mb-3">
              <div className="col-12 col-sm-7">
                <Form.Group>
                  <Form.Label className="small fw-semibold text-muted mb-1.5">
                    Dirección de Entrega
                  </Form.Label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0 text-muted">
                      <i className="bi bi-geo-alt"></i>
                    </span>
                    <Form.Control
                      type="text"
                      name="d_domic"
                      placeholder="Calle, Altura, Piso / Dpto"
                      value={registerData.d_domic}
                      onChange={handleRegisterChange}
                      className="border-start-0"
                    />
                  </div>
                </Form.Group>
              </div>
              <div className="col-12 col-sm-5">
                <Form.Group>
                  <Form.Label className="small fw-semibold text-muted mb-1.5">
                    Localidad / Ciudad
                  </Form.Label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0 text-muted">
                      <i className="bi bi-map"></i>
                    </span>
                    <Form.Control
                      type="text"
                      name="d_local"
                      placeholder="Ej. Lanús / CABA"
                      value={registerData.d_local}
                      onChange={handleRegisterChange}
                      className="border-start-0"
                    />
                  </div>
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3.5">
              <Form.Label className="small fw-semibold text-muted mb-1.5">
                Rubro o Notas adicionales
              </Form.Label>
              <Form.Control
                type="text"
                name="d_obser"
                placeholder="Ej. Librería, Gastronómico, Empresa, etc."
                value={registerData.d_obser}
                onChange={handleRegisterChange}
              />
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              disabled={isLoading}
              className="w-100 py-2.5 rounded-pill fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2 mt-2"
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" animation="border" />
                  <span>Registrando...</span>
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle-fill"></i>
                  <span>Registrarme y Ver Precios</span>
                </>
              )}
            </Button>
          </Form>
        )}

        {/* Pestaña: Identificación / Login de Cliente Existente (Email y Contraseña) */}
        {activeTab === 'login' && (
          <Form onSubmit={handleLoginSubmit}>
            <p className="small text-muted mb-3.5 lh-sm">
              Ingresá tu correo electrónico y tu contraseña para acceder a tus precios y finalizar tus pedidos.
            </p>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold text-dark mb-1.5">
                Correo Electrónico Registrado <span className="text-danger">*</span>
              </Form.Label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0 text-primary">
                  <i className="bi bi-envelope-fill"></i>
                </span>
                <Form.Control
                  type="email"
                  placeholder="ejemplo@correo.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  autoFocus
                  required
                  className="border-start-0"
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3.5">
              <Form.Label className="small fw-semibold text-dark mb-1.5">
                Contraseña <span className="text-danger">*</span>
              </Form.Label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0 text-primary">
                  <i className="bi bi-lock-fill"></i>
                </span>
                <Form.Control
                  type={showLoginPassword ? "text" : "password"}
                  maxLength={15}
                  placeholder="Ingresá tu contraseña"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  className="border-start-0 border-end-0"
                />
                <span
                  className="input-group-text bg-white border-start-0 text-muted"
                  style={{ cursor: 'pointer', transition: 'color 0.15s ease' }}
                  onClick={() => setShowLoginPassword(!showLoginPassword)}
                  title={showLoginPassword ? "Ocultar contraseña" : "Ver contraseña"}
                >
                  <i className={`bi ${showLoginPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                </span>
              </div>
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              disabled={isLoading}
              className="w-100 py-2.5 rounded-pill fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2 mt-3"
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" animation="border" />
                  <span>Verificando...</span>
                </>
              ) : (
                <>
                  <i className="bi bi-box-arrow-in-right"></i>
                  <span>Ingresar a mi Cuenta</span>
                </>
              )}
            </Button>

            <div className="text-center mt-3 pt-1">
              <span className="small text-muted">¿Todavía no te registraste? </span>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 fw-semibold text-decoration-none"
                onClick={() => { setActiveTab('register'); setErrorMessage(''); }}
              >
                Creá tu cuenta acá
              </button>
            </div>
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0 px-4 pb-3 justify-content-center">
        <span className="small text-muted d-flex align-items-center gap-1" style={{ fontSize: '0.73rem' }}>
          <i className="bi bi-shield-check text-success fs-6"></i>
          <span>Tus datos se guardan de forma segura para gestionar tus pedidos.</span>
        </span>
      </Modal.Footer>
    </Modal>
  );
};

export default EcommerceAuthModal;

